import { axiosInstance } from "@/lib/axios";
import type { GetWalletsParams, WalletBalanceResponse, WalletsResponse, SwapPayload, SwapResponse, TransactionsResponse, GetTransactionsParams, PaymentPayload, PaymentResponse, WithdrawPayload, RedeemCardResponse, WithdrawInitiateResponse, VerifyWithdrawPayload, VerifyWithdrawResponse, ConfirmWithdrawPayload, ConfirmWithdrawResponse } from "./types";

export async function getWallets(params: GetWalletsParams = {}): Promise<WalletsResponse> {
  const p = new URLSearchParams();
  if (params.network) p.set("network", String(params.network));

  const { data } = await axiosInstance.get<WalletsResponse>(`/account/wallet?${p.toString()}`);
  return data;
}

export async function getWalletBalance(walletAddress: string): Promise<WalletBalanceResponse> {
  const { data } = await axiosInstance.get<WalletBalanceResponse>(`/account/wallet/${walletAddress}/balance`);
  return data;
}

/**
 * V2 — scoped by BOTH wallet address and network id, unlike `getWalletBalance`
 * above. The old address-only endpoint returns every network's assets on each
 * of a shared address's `Wallet` rows identically, which is what forced the
 * client-side re-scoping in `enrich-wallet-assets.ts` (confirmed: a real ~50
 * USDT balance rendered as ~96 in the navbar popover). Use this for every
 * surface that displays a specific wallet's balance; `getWalletBalance` stays
 * for call sites that are not balance-display (none currently exist).
 */
export async function getWalletBalanceV2(
  walletAddress: string,
  networkId: number
): Promise<WalletBalanceResponse> {
  const { data } = await axiosInstance.get<WalletBalanceResponse>(
    `/account/wallet/V2/${walletAddress}/${networkId}/balance`
  );
  return data;
}

export async function getTransactions(params: GetTransactionsParams = {}): Promise<TransactionsResponse> {
  const p = new URLSearchParams();
  if (params.page) p.set("page", String(params.page));
  if (params.per_page) p.set("per_page", String(params.per_page));
  if (params.network) p.set("network", String(params.network));
  if (params.type) p.set("type", params.type);

  const { data } = await axiosInstance.get<TransactionsResponse>(`/account/wallet-transactions/history?${p.toString()}`);
  return data;
}

export async function submitWithdraw(payload: WithdrawPayload): Promise<WithdrawInitiateResponse> {
  const { data } = await axiosInstance.post<WithdrawInitiateResponse>(
    "/account/wallet-transactions/withdraw",
    payload,
  );
  return data;
}

export async function verifyWithdraw(payload: VerifyWithdrawPayload): Promise<VerifyWithdrawResponse> {
  const { data } = await axiosInstance.post<VerifyWithdrawResponse>("/account/withdraw/verify", payload);
  return data;
}

/**
 * External-branch only. Called once the wallet's transaction has reached
 * on-chain success — never before, and never with a fabricated `trx_hash`.
 * Retrying THIS call with the same `withdraw_id`/`trx_hash` is the documented
 * way to ride out the backend's own indexing delay (see
 * `isRetryableConfirmError` in `external-withdraw-flow.ts`); it never
 * resubmits the withdrawal or the blockchain transaction.
 */
export async function confirmWithdraw(payload: ConfirmWithdrawPayload): Promise<ConfirmWithdrawResponse> {
  const { data } = await axiosInstance.post<ConfirmWithdrawResponse>("/account/withdraw/confirm", payload);
  return data;
}

/**
 * Redeems a random-card token — the gift/voucher code handed to a user over
 * email or SMS — and credits their wallet with it.
 *
 * Errors are intentionally NOT swallowed: an invalid or already-used code is
 * the single most likely outcome, and the caller needs the server's own
 * message ("already redeemed", "expired", …) to show the user.
 */
export async function redeemRandomCard(token: string): Promise<RedeemCardResponse> {
  const { data } = await axiosInstance.post<RedeemCardResponse>(
    "/account/wallet-transactions/random-card",
    { token: token.trim() },
    // This form surfaces failures inline in its own UI, so the global
    // error-toast interceptor would only duplicate them.
    { suppressToast: true } as any,
  );
  return data;
}

export async function swapCurrency(payload: SwapPayload): Promise<SwapResponse> {
  const { data } = await axiosInstance.post<SwapResponse>("/account/wallet-transactions/swap", payload);
  return data;
}

/** Quiet: a refusal names its amount in rials, so the deposit flow says it itself, in toman (`lib/rial-text`). */
export async function createPayment(payload: PaymentPayload): Promise<PaymentResponse> {
  const { data } = await axiosInstance.post<PaymentResponse>("/account/payment", payload, { suppressToast: true } as any);
  return data;
}

export type LinkWalletPayload = {
  network: string;
  wallet: string;
  wallet_address: string;
  wallet_type: string;
  name: string;
  lang: string;
};

export async function linkWallet(payload: LinkWalletPayload): Promise<any> {
  // Try to use the same endpoint as profile update, assuming backend handles it
  // or use the user provided path if it exists on the new backend.
  // Given the project structure, /auth/profile seems to be the place.
  const { data } = await axiosInstance.post("/auth/profile", payload);
  return data;
}
